import { UseFormReturn, Controller } from "react-hook-form";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Button from "@/components/Button";
import MultiSelect from "@/components/MultiSelect";
import classNames from "classnames";

interface EnumOption {
  name: string;
  description: string;
}

interface EventDetailsFormData {
  recruitmentEventType: string;
  purpose: string;
  pgaRepresentation: string[];
  supportRequested: string[];
  additionalComments: string;
}

interface EditEventDetailsCardProps {
  form: UseFormReturn<EventDetailsFormData>;
  onSubmit: (formData: EventDetailsFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  enumOptionsData?: {
    recruitmentEventTypeOptions?: {
      enumValues: EnumOption[];
    };
    purposeOptions?: {
      enumValues: EnumOption[];
    };
    pgaRepresentationOptions?: {
      enumValues: EnumOption[];
    };
    supportRequestedOptions?: {
      enumValues: EnumOption[];
    };
  };
}

export default function EditEventDetailsCard({
  form,
  onSubmit,
  onCancel,
  isSubmitting,
  enumOptionsData
}: EditEventDetailsCardProps) {
  return (
    <div className="space-y-6 w-full">
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="recruitmentEventType"
          render={({ field }) => (
            <Select
              label="Recruitment Event Type"
              options={
                enumOptionsData?.recruitmentEventTypeOptions?.enumValues?.map((option) => ({
                  label: option.description,
                  value: option.name
                })) || []
              }
              value={field.value}
              onSelect={(value: string) => field.onChange(value)}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="purpose"
          render={({ field }) => (
            <Select
              label="Purpose"
              options={
                enumOptionsData?.purposeOptions?.enumValues?.map((option) => ({
                  label: option.description,
                  value: option.name
                })) || []
              }
              value={field.value}
              onSelect={(value: string) => field.onChange(value)}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="pgaRepresentation"
          render={({ field }) => (
            <MultiSelect
              label="PGA Representation"
              options={
                enumOptionsData?.pgaRepresentationOptions?.enumValues?.map((option) => ({
                  label: option.description,
                  value: option.name
                })) || []
              }
              selectedValues={field.value}
              onSelect={(value: string, checked: boolean) => {
                const currentValues = field.value || [];
                const newValues = checked
                  ? [...currentValues, value]
                  : currentValues.filter((v: string) => v !== value);
                field.onChange(newValues);
              }}
              onClear={() => field.onChange([])}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="supportRequested"
          render={({ field }) => (
            <MultiSelect
              label="Support Requested"
              options={
                enumOptionsData?.supportRequestedOptions?.enumValues?.map((option) => ({
                  label: option.description,
                  value: option.name
                })) || []
              }
              selectedValues={field.value}
              onSelect={(value: string, checked: boolean) => {
                const currentValues = field.value || [];
                const newValues = checked
                  ? [...currentValues, value]
                  : currentValues.filter((v: string) => v !== value);
                field.onChange(newValues);
              }}
              onClear={() => field.onChange([])}
            />
          )}
        />
      </div>
      <div className="col-span-full">
        <Controller
          control={form.control}
          name="additionalComments"
          render={({ field }) => (
            <TextField
              label="Additional Comments"
              type="textarea"
              rows={4}
              className="w-full"
              value={field.value}
              onChange={(e) => field.onChange((e.target as HTMLInputElement)?.value || "")}
            />
          )}
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outlined" disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          iconClassName={classNames(isSubmitting && "animate-spin w-6 h-6 !block")}
          disabled={isSubmitting}
          icon={isSubmitting ? "progress_activity" : undefined}
          onClick={() => onSubmit(form.getValues())}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
